import { conversionFlowHasConclusiveLaterStep, evaluateConversionFlow } from "./conversion-flow";
import { holdInteractiveFlowChecks } from "./cro-flow-probes";
import { peopleCheckState } from "./people-score";
import { CRO_V5_VERSION, type CategoryScore, type CheckResult, type ReportSnapshot, type SiteSnapshot } from "./types";

export const CRO_COVERAGE_THRESHOLD = 0.5;
export const CRO_MIN_CONCLUSIVE = 2;
export const CRO_MIN_ELIGIBLE_BENCHMARKS = 2;

export const CRO_BENCHMARK_FIRST_IMPRESSION = "First Impression";
export const CRO_BENCHMARK_CTA = "CTA Effectiveness";
export const CRO_BENCHMARK_TRUST = "Trust Signals";
export const CRO_BENCHMARK_MOBILE = "Mobile Readiness";
export const CRO_BENCHMARK_FLOW = "Conversion Flow";

export type CroBenchmarkId =
  | "first_impression"
  | "cta"
  | "trust"
  | "mobile"
  | "conversion_flow";

export const CRO_BENCHMARKS: { id: CroBenchmarkId; label: string; question: string; description: string }[] = [
  {
    id: "first_impression",
    label: CRO_BENCHMARK_FIRST_IMPRESSION,
    question: "Can a new visitor tell who you are, what you offer, and who it is for?",
    description: "Headline, offering, audience, and service list on the public page.",
  },
  {
    id: "cta",
    label: CRO_BENCHMARK_CTA,
    question: "Is the next step specific and reachable?",
    description: "Call-to-action language, destination, contact path, and booking.",
  },
  {
    id: "trust",
    label: CRO_BENCHMARK_TRUST,
    question: "Is there proof, identity, privacy, and honest AI disclosure?",
    description: "Reviews, credentials, privacy, and AI disclosure when a bot is present.",
  },
  {
    id: "mobile",
    label: CRO_BENCHMARK_MOBILE,
    question: "Can someone complete the path on a phone?",
    description: "Viewport, navigation, tap targets. Core Web Vitals stay not verified without a lab.",
  },
  {
    id: "conversion_flow",
    label: CRO_BENCHMARK_FLOW,
    question: "Can a visitor complete entry through confirmation?",
    description: "Entry is not enough. Later steps stay not verified until they are actually tested.",
  },
];

const CHECK_BENCHMARK: Record<string, CroBenchmarkId> = {
  primary_headline: "first_impression",
  offering_described: "first_impression",
  audience_or_area: "first_impression",
  services_listed: "first_impression",
  "P-A01": "first_impression",
  "P-A02": "first_impression",
  "P-A03": "first_impression",
  cta_language: "cta",
  cta_destination: "cta",
  contact_or_booking: "cta",
  above_fold_cta: "cta",
  "P-L01": "cta",
  "P-L02": "cta",
  "P-L04": "cta",
  reviews_present: "trust",
  credentials_or_team: "trust",
  privacy_link: "trust",
  "P-C06": "trust",
  ai_disclosure: "trust",
  viewport_meta: "mobile",
  mobile_nav: "mobile",
  core_web_vitals: "mobile",
  responsive_inputs: "mobile",
  "CF-ENTRY": "conversion_flow",
  "CF-INTERACT": "conversion_flow",
  "CF-INTENT": "conversion_flow",
  "CF-CAPTURE": "conversion_flow",
  "CF-ACTION": "conversion_flow",
  "CF-HANDOFF": "conversion_flow",
  "CF-CONFIRM": "conversion_flow",
  "CF-DEADEND": "conversion_flow",
  "CF-CONTEXT": "conversion_flow",
  "CF-BROKEN": "conversion_flow",
  form_errors: "conversion_flow",
  "P-L03": "conversion_flow",
  "P-L05": "conversion_flow",
  "P-R05": "conversion_flow",
  "P-R06": "conversion_flow",
};

export type CroBenchmarkScore = {
  id: CroBenchmarkId;
  label: string;
  score: number | null;
  rawScore: number | null;
  coverage: number;
  conclusive: number;
  applicable: number;
};

export type CroScoreResult = {
  score: number | null;
  rawScore: number | null;
  coverage: number;
  confidence: number;
  categories: CroBenchmarkScore[];
  insufficientEvidence: boolean;
  benchmarksScored: number;
  flowTested: boolean;
};

const SPEC = "https://www.siteglue.ai/audits/conversion-optimization";

function extraTrustAndMobileChecks(existing: CheckResult[], pageUrl: string): CheckResult[] {
  const hasChat = existing.some((check) => check.id === "P-C01" && check.status === "passed")
    || existing.some((check) => check.id === "live_assistance" && check.status === "passed");
  const disclosure: CheckResult = {
    id: "ai_disclosure",
    title: "AI disclosure",
    category: CRO_BENCHMARK_TRUST,
    specUrl: SPEC,
    maturity: "emerging",
    detection: "html",
    weight: 1,
    impact: "Visitors should know when they are talking to an AI.",
    status: hasChat ? "not_tested" : "not_applicable",
    evidence: hasChat
      ? "A chat or assistance widget is present. AI disclosure was not verified from this scan."
      : "No chat widget was found, so AI disclosure does not apply.",
    verification: hasChat ? "not_verified" : "not_applicable",
    confidence: "low",
    pageUrl,
  };
  const responsive: CheckResult = {
    id: "responsive_inputs",
    title: "Responsive form inputs",
    category: CRO_BENCHMARK_MOBILE,
    specUrl: SPEC,
    maturity: "emerging",
    detection: "html",
    weight: 1,
    impact: "Form fields should be usable on a phone.",
    status: "not_tested",
    evidence: "Tap targets and input types were not measured in a mobile lab, so this stays not verified.",
    verification: "not_verified",
    confidence: "low",
    pageUrl,
  };
  return [disclosure, responsive];
}

export function isCroV5(report: Pick<ReportSnapshot, "version"> | null | undefined): boolean {
  return report?.version === CRO_V5_VERSION;
}

export function croBenchmarkForCheck(id: string): CroBenchmarkId | null {
  return CHECK_BENCHMARK[id] ?? null;
}

function withCroCategory(check: CheckResult): CheckResult {
  const benchmark = croBenchmarkForCheck(check.id);
  if (!benchmark) return check;
  const label = CRO_BENCHMARKS.find((item) => item.id === benchmark)?.label ?? check.category;
  return { ...check, category: label };
}

function checkState(check: CheckResult): ReturnType<typeof peopleCheckState> {
  return peopleCheckState(check);
}

function scoreBenchmark(checks: CheckResult[], id: CroBenchmarkId): CroBenchmarkScore {
  const label = CRO_BENCHMARKS.find((item) => item.id === id)?.label ?? id;
  const mapped = checks.filter((check) => croBenchmarkForCheck(check.id) === id);
  let applicable = 0;
  let conclusive = 0;
  let passed = 0;
  for (const check of mapped) {
    const state = checkState(check);
    if (state === "not_applicable") continue;
    applicable += 1;
    if (state === "not_verified") continue;
    conclusive += 1;
    if (state === "verified") passed += 1;
  }
  const coverage = applicable === 0 ? 0 : conclusive / applicable;
  const enough = coverage >= CRO_COVERAGE_THRESHOLD && conclusive >= CRO_MIN_CONCLUSIVE;
  let rawScore: number | null = conclusive === 0 || !enough ? null : (passed / conclusive) * 100;
  if (id === "conversion_flow" && !conversionFlowHasConclusiveLaterStep(mapped)) {
    rawScore = null;
  }
  return {
    id,
    label,
    score: rawScore == null ? null : Math.round(rawScore),
    rawScore,
    coverage,
    conclusive,
    applicable,
  };
}

export function scoreCroV5(checks: CheckResult[]): CroScoreResult {
  const categories = CRO_BENCHMARKS.map((benchmark) => scoreBenchmark(checks, benchmark.id));
  const eligible = categories.filter((category) => category.score != null);
  const coverage =
    categories.reduce((sum, category) => sum + category.coverage, 0) / Math.max(categories.length, 1);
  const flow = categories.find((category) => category.id === "conversion_flow");
  const flowTested = conversionFlowHasConclusiveLaterStep(checks);
  if (eligible.length < CRO_MIN_ELIGIBLE_BENCHMARKS) {
    return {
      score: null,
      rawScore: null,
      coverage,
      confidence: coverage,
      categories,
      insufficientEvidence: true,
      benchmarksScored: eligible.length,
      flowTested,
    };
  }
  const rawScore = eligible.reduce((sum, category) => sum + (category.rawScore ?? category.score ?? 0), 0) / eligible.length;
  return {
    score: Math.round(rawScore),
    rawScore,
    coverage,
    confidence: coverage,
    categories,
    insufficientEvidence: false,
    benchmarksScored: eligible.length,
    flowTested: Boolean(flow && flowTested),
  };
}

function categoryRows(checks: CheckResult[], scored: CroScoreResult): CategoryScore[] {
  return scored.categories.map((category) => {
    const mapped = checks.filter((check) => croBenchmarkForCheck(check.id) === category.id);
    return {
      category: category.label,
      score: category.score,
      passed: mapped.filter((check) => checkState(check) === "verified").length,
      failed: mapped.filter((check) => checkState(check) === "missing").length,
      warnings: 0,
      notApplicable: mapped.filter((check) => checkState(check) === "not_applicable").length,
      notTested: mapped.filter((check) => checkState(check) === "not_verified").length,
    };
  });
}

export function buildCroV5Report(
  evidence: ReportSnapshot,
  flowChecks: CheckResult[],
  pageUrl = "",
  probedIds: ReadonlySet<string> = new Set(),
): ReportSnapshot {
  const extras = extraTrustAndMobileChecks(evidence.checks, pageUrl);
  const checks = holdInteractiveFlowChecks(
    [...evidence.checks, ...extras, ...flowChecks].map(withCroCategory),
    probedIds,
  );
  return rebuildCroV5Snapshot(checks, evidence.industry);
}

export function rebuildCroV5Snapshot(checks: CheckResult[], industry?: string): ReportSnapshot {
  const remapped = checks.map(withCroCategory);
  const scored = scoreCroV5(remapped);
  return {
    version: CRO_V5_VERSION,
    score: scored.score,
    coverage: {
      checked: remapped.length,
      applicable: scored.categories.reduce((sum, category) => sum + category.applicable, 0),
      notTested: remapped.filter((check) => checkState(check) === "not_verified").length,
      notApplicable: remapped.filter((check) => checkState(check) === "not_applicable").length,
    },
    categories: categoryRows(remapped, scored),
    checks: remapped,
    ...(industry ? { industry } : {}),
    confidence: scored.confidence,
    benchmarksScored: scored.benchmarksScored,
    flowTested: scored.flowTested,
  };
}

export function evaluateCroV5FromEvidence(
  evidence: ReportSnapshot,
  snapshot: SiteSnapshot,
): ReportSnapshot {
  return buildCroV5Report(evidence, evaluateConversionFlow(snapshot), snapshot.finalUrl);
}

export function croBarsForReport(report: ReportSnapshot | null | undefined): {
  id: CroBenchmarkId;
  label: string;
  checkIds: readonly string[];
  score: number | null;
}[] {
  const scored = scoreCroV5(report?.checks ?? []);
  return scored.categories.map((category) => ({
    id: category.id,
    label: category.label,
    checkIds: (report?.checks ?? [])
      .filter((check) => croBenchmarkForCheck(check.id) === category.id)
      .map((check) => check.id),
    score: category.score,
  }));
}
